import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/auth.store';
import { useSelectedCompanyStore } from '../../stores/selectedCompany.store';
import { useUserCompaniesStore } from '../../stores/userCompanies.store';
import {
  useGetDiffusedCount,
  useGetOpenedRatio,
  useGetValidatedCount,
  useGetRecentDocuments,
} from '../../services/dashboard.service';
import { useGetDocumentPresignedUrl } from '../../services/documents.service';
import { DashboardWidget } from './DashboardWidget';
import { RecentDocumentsTable } from './RecentDocumentsTable';
import { ResendDocumentModal, type ResendDocumentModalRef } from '../documents/ResendDocumentModal';
import { UserCompanyRole } from '@monorepo/shared';

const getRoleBadgeConfig = (role: UserCompanyRole | undefined) => {
  switch (role) {
    case UserCompanyRole.SUPER_ADMIN:
      return { className: 'bg-red-500 text-white', labelKey: 'dashboard.role.super_admin' };
    case UserCompanyRole.SUPER_USER:
      return { className: 'bg-orange-400 text-white', labelKey: 'dashboard.role.super_user' };
    default:
      return { className: 'bg-orange-400 text-white', labelKey: 'dashboard.role.super_user' };
  }
};

export const AdminDashboard = () => {
  const { t } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const selectedCompanyId = useSelectedCompanyStore((state) => state.selectedCompanyId);
  const companies = useUserCompaniesStore((state) => state.companies);
  const currentUserRole = companies.find((c) => c.id === selectedCompanyId)?.role;
  const badgeConfig = getRoleBadgeConfig(currentUserRole);

  const resendDocumentModalRef = useRef<ResendDocumentModalRef>(null);

  const {
    data: diffusedData,
    isLoading: isLoadingDiffused,
    isError: isErrorDiffused,
  } = useGetDiffusedCount(selectedCompanyId ?? '');

  const {
    data: openedRatioData,
    isLoading: isLoadingOpenedRatio,
    isError: isErrorOpenedRatio,
  } = useGetOpenedRatio(selectedCompanyId ?? '');

  const {
    data: validatedData,
    isLoading: isLoadingValidated,
    isError: isErrorValidated,
  } = useGetValidatedCount(selectedCompanyId ?? '');

  const { data: recentDocs, isLoading: isLoadingDocs } = useGetRecentDocuments(
    selectedCompanyId ?? '',
  );
  const getPresignedUrlMutation = useGetDocumentPresignedUrl(selectedCompanyId ?? '');

  const openRatio = openedRatioData ? `${openedRatioData.opened}/${openedRatioData.total}` : '0/0';
  const validatedCount = validatedData?.count ?? 0;
  const diffusedCount = diffusedData?.count ?? 0;
  const pendingCount = openedRatioData?.total ?? 0;

  const handleOpenDocument = async (nodeId: string) => {
    try {
      const result = await getPresignedUrlMutation.mutateAsync(nodeId);
      window.open(result.url, '_blank');
    } catch (error) {
      console.error('Error opening document:', error);
    }
  };

  const handleResendDocument = (documentId: string) => {
    resendDocumentModalRef.current?.open(documentId);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="mb-8">
        <div className="flex flex-wrap items-center gap-3 mb-1">
          <h1 className="text-2xl sm:text-3xl font-display text-primary">
            {t('dashboard.greeting', { name: user?.firstName })}
          </h1>
          <span className={`px-3 py-1 text-xs font-medium rounded-full ${badgeConfig.className}`}>
            {t(badgeConfig.labelKey)}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        <DashboardWidget
          value={validatedCount}
          label={t('dashboard.widgets.validated.label', { count: validatedCount })}
          linkText={t('dashboard.widgets.validated.link', { count: validatedCount })}
          linkTo="/documents/filtered/validated"
          variant="highlight"
          isLoading={isLoadingValidated}
          isError={isErrorValidated}
        />
        <DashboardWidget
          value={diffusedCount}
          label={t('dashboard.widgets.diffused.label', { count: diffusedCount })}
          linkText={t('dashboard.widgets.diffused.link', { count: diffusedCount })}
          linkTo="/documents/filtered/diffused"
          variant="blue"
          isLoading={isLoadingDiffused}
          isError={isErrorDiffused}
        />
        <DashboardWidget
          value={openRatio}
          label={t('dashboard.widgets.pending_opening.label', { count: pendingCount })}
          linkText={t('dashboard.widgets.pending_opening.link', { count: pendingCount })}
          linkTo="/documents/filtered/pending"
          variant="default"
          isLoading={isLoadingOpenedRatio}
          isError={isErrorOpenedRatio}
        />
      </div>

      <RecentDocumentsTable
        documents={recentDocs?.documents ?? []}
        isLoading={isLoadingDocs}
        onOpenDocument={handleOpenDocument}
        onResendDocument={handleResendDocument}
      />

      <ResendDocumentModal ref={resendDocumentModalRef} companyId={selectedCompanyId ?? ''} />
    </div>
  );
};
